import { CategoryField } from './operation-fields.js';
/**
 * «Документ»: загрузка файла вместо съёмки (§20 плана миграции) — кассовый
 * чек, квитанция или снимок экрана банка, выписка по счёту.
 *
 * Весь экран держится на трёх правилах, и каждое защищает деньги человека.
 *
 * · **Метка видна до выбора файла.** Сказать «нужен Pro» после того, как
 *   человек нашёл документ в почте и дождался загрузки, значит отнять минуту
 *   и доверие разом.
 * · **До подтверждения баланс не меняется.** Разобранный документ живёт
 *   отдельно от операций, пока человек не сказал «да».
 * · **Отказ никогда не тупик.** Из любого состояния есть выход в ручную
 *   запись: сумму человек знает и без нас.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  checkItemsTotal, checkReceiptFile, classifyItemPart, formatMoney, kop, parseFiscalText,
  rub, ZERO,
  SUPPORTED_RECEIPT_TYPES, MAX_RECEIPT_BYTES,
  type FiscalReceipt, type ImportedReceipt, type ImportVerdict, type MatchCandidate,
  type Money, type PlainDate, type PlanPart,
} from '@skoka/core';
import { SheetWrap } from './sheet.js';
import { Tip } from './tip.js';
import { fetchAccess, onAccessChanged, featureView, UNKNOWN_ACCESS, type AccessState } from './access.js';
import { looksLikeStatement, prepareDocument, renderDocument } from './document.js';
import { categorizeBasket, classifyBasket, parseDocument, type StatementOp } from './ai.js';
import { importAccountId } from './import-account.js';
import { StatementReview } from './statement.js';
import { ReceiptBar, ReceiptHead, ReceiptItems, ReceiptSelectors, SplitBanner } from './receipt-view.js';
import {
  confirmReceiptImport, dropReceiptImport, knownReceipt, linkReceiptImport, receiveReceipt, todayPd,
  type AppState,
} from './store.js';
import { isNewCategory, modelCatalog, topLevel, type Category } from './categories.js';
import { nameKey, rememberedCategories } from './category-memory.js';
import { dayMonthYear, plural } from './text.js';
import { NewCatsAsk, NewCatsLines, NewModeQuestion, useNewCategories, type NewCategories } from './new-categories.js';
import { flash } from './flash.js';

/** Какие файлы принимает «Файл» — и здесь, и кнопкой в записи операции. */
export const DOC_ACCEPT = SUPPORTED_RECEIPT_TYPES.join(',');

type Stage =
  | { kind: 'pick' }
  | { kind: 'working'; step: string }
  | { kind: 'rejected'; message: string }
  /** Документ разобран и ждёт человека. Операции ещё нет. */
  | {
    kind: 'review'; receipt: ImportedReceipt; importId: string; matches: MatchCandidate[];
    /** Итога в документе не было — сложили позиции. Человеку это показываем. */
    computed: boolean;
  }
  /** Снимок экрана банка: каждая строка — своя операция, человек отмечает нужные. */
  | { kind: 'statement'; operations: StatementOp[]; dropped: number }
  /** Повтор, возврат, нераспознанный итог — всё, что покупкой не становится. */
  | { kind: 'refused'; message: string };

export function ReceiptDoc({ state, file, initialAccountId, onClose, onManual, onPlans, onSaved }: {
  state: AppState;
  /**
   * Файл уже выбран — кнопкой «Файл» в записи операции. Тогда шага «Выбрать
   * файл» нет: человек нажал «Файл» и сразу попал в выбор, откуда его взять.
   */
  file?: File;
  initialAccountId?: string;
  onClose: () => void;
  /** «Ввести расход вручную» — выход из любого отказа. */
  onManual: () => void;
  onPlans: () => void;
  onSaved: () => void;
}) {
  // `null` — ещё не знаем. Пустое состояние доступа означает «нет сети», и
  // до ответа сервера окно на миг говорило «Нужна сеть», хотя сеть была.
  const [access, setAccess] = useState<AccessState | null>(null);
  const [stage, setStage] = useState<Stage>(file ? { kind: 'working', step: 'Готовлю страницы' } : { kind: 'pick' });
  /** Записано — плашкой «Записано…» и сразу назад, без экрана с одной кнопкой «Готово» (28.10.2026). */
  const finish = (message: string) => { flash(message); onClose(); };
  const spendable = state.accounts.filter((a) => !a.archived);
  const [accId, setAccId] = useState(
    importAccountId(state.accounts, initialAccountId),
  );
  const [categories, setCategories] = useState(state.categories);
  const cats = topLevel(categories);
  /** Категория всего чека — когда делить его по позициям нечем. */
  const [catId, setCatId] = useState(
    cats.find((c) => c.id === 'groceries')?.id ?? cats[0]?.id ?? 'other',
  );
  /**
   * Часть плана для каждой позиции. Нужна только там, где категория
   * смешанная: у остальных её задаёт сама категория, как при ручной записи.
   * Словарь отвечает сразу, модель — следом.
   */
  const [parts, setParts] = useState<PlanPart[]>([]);
  /**
   * Категория каждой позиции: в одном пакете бывают продукты и бытовая химия.
   * Модель предлагает из справочника человека, человек правит тапом; его
   * правка старше любой догадки: он смотрит на покупку, а разбор — на
   * сокращённое название.
   */
  const [itemCats, setItemCats] = useState<string[]>([]);
  /** Категорию подтвердила модель или человек; иначе строка отмечена. */
  const [sure, setSure] = useState<boolean[]>([]);
  const [sorting, setSorting] = useState(false);
  const touched = useRef<Set<number>>(new Set());
  /** Новые категории для позиций, которым в справочнике нет места. */
  const nc = useNewCategories({
    mode: state.newCategoryMode, categories, onCategories: setCategories, ids: itemCats,
    touched: (n) => touched.current.has(n),
    apply: (changes) => {
      setItemCats((prev) => prev.map((c, n) => changes.get(n) ?? c));
      setSure((prev) => prev.map((x, n) => x || changes.has(n)));
    },
  });
  const catalog = useMemo(() => modelCatalog(categories), [categories]);
  /** Категории, которые человек уже давал этим позициям в прошлых чеках. */
  const memory = useMemo(() => rememberedCategories(state.transactions, categories), [state.transactions, categories]);
  /** Картинка документа для «Просмотреть»: сверить позиции с самим чеком. */
  const [preview, setPreview] = useState<string | null>(null);
  useEffect(() => () => { if (preview?.startsWith('blob:')) URL.revokeObjectURL(preview); }, [preview]);
  const partOf = (n: number): PlanPart =>
    nc.all.find((c) => c.id === itemCats[n])?.defaultPart ?? parts[n] ?? 'need';

  useEffect(() => { void fetchAccess().then(setAccess); }, []);
  // Включили пробный или оплатили — метки «Pro» снимаются сразу, без перезагрузки.
  useEffect(() => onAccessChanged(() => { void fetchAccess().then(setAccess); }), []);
  const view = featureView('receipt_import', access ?? UNKNOWN_ACCESS);

  const take = async (file: File | undefined): Promise<void> => {
    if (!file) return;
    // Формат и размер проверяются до отправки: отказ по мобильной сети после
    // десяти мегабайт — худший момент для такого сообщения.
    const check = checkReceiptFile({ type: file.type, size: file.size });
    if (!check.ok) { setStage({ kind: 'rejected', message: check.message }); return; }

    try {
      setStage({ kind: 'working', step: 'Готовлю страницы' });
      const doc = await prepareDocument(file);
      const page = doc.pages[0];
      setPreview(file.type.startsWith('image/') ? URL.createObjectURL(file)
        : page ? `data:${page.mimeType};base64,${page.base64}` : null);

      // Повтор ловится до разбора: иначе второе нажатие на тот же файл стоит
      // денег за уже сделанную работу.
      const seen = await knownReceipt(doc.fileHash);
      if (seen) {
        setStage({
          kind: 'refused',
          message: 'Этот чек уже загружен — вторая покупка из него не создаётся',
        });
        return;
      }

      setStage({ kind: 'working', step: 'Читаю документ' });

      // Напечатанный чек читается как есть: числа берутся из документа, а не
      // восстанавливаются из картинки. Модель здесь не нужна вовсе — и не
      // зовётся, пока текст сходится сам с собой.
      const printed = doc.lines.length > 0 ? parseFiscalText(doc.lines) : null;
      const statement = !printed?.trusted && looksLikeStatement(doc.lines);
      if (statement) setStage({ kind: 'working', step: 'Читаю выписку' });
      const read: Read = printed?.trusted
        ? { receipt: fromPrinted(doc.fileHash, printed), computed: printed.totalFromItems,
          operations: printed.kind === 'refund' ? refundOp(printed) : [], dropped: 0 }
        : statement ? fromParsed(doc.fileHash, await parseDocument({ text: doc.pageLines, fileHash: doc.fileHash, today: todayPd() }))
          : await fromPictures(file, doc);
      const { receipt, computed } = read;
      setAccId((selected) => importAccountId(state.accounts, selected, read.account));

      // Всё, что не чек покупки, — снимок экрана банка, выписка, квитанция,
      // возврат, компенсация, записка с суммами — показывается операциями.
      // Складывать их строки в покупку нельзя, но каждую строку человек может
      // записать отдельно: расходом, поступлением, переводом или никак.
      // Документом они не сохраняются: повтор ловится по каждой операции.
      if (receipt.kind !== 'sale') {
        setStage(read.operations.length > 0
          ? { kind: 'statement', operations: read.operations, dropped: read.dropped }
          : { kind: 'refused', message: 'Операции в документе не прочитались — попробуй другой файл или запиши вручную' });
        return;
      }

      const { verdict, importId } = await receiveReceipt(receipt);
      const names = receipt.items?.map((i) => i.name) ?? [];
      setParts(names.map((n) => classifyItemPart(n)));
      // Знакомая позиция — в категорию, которую человек ей уже давал; модель её не трогает.
      const known = names.map((n) => memory.items.get(nameKey(n)) ?? null);
      touched.current = new Set(known.flatMap((c, n) => (c ? [n] : [])));
      setItemCats(known.map((c) => c ?? catId));
      setSure(known.map((c) => c !== null));
      const next = afterVerdict(verdict, receipt, importId, computed);
      setStage(next);

      // Модель уточняет разложенное словарём и подбирает категории. Ответа
      // можно не дождаться — тогда категорию выберут тапом. Повтор и отказ
      // модель не зовут: раскладывать там нечего.
      if (next.kind === 'review' && names.length > 0) {
        void classifyBasket(names).then((byModel) => {
          if (!byModel) return;
          setParts((prev) => prev.map((p, n) => byModel[n] ?? p));
        });
        setSorting(true);
        void categorizeBasket(names, catalog).then((byModel) => {
          setSorting(false);
          if (!byModel) return;
          setItemCats((prev) => prev.map((c, n) => (touched.current.has(n) ? c : byModel[n] ?? c)));
          setSure((prev) => prev.map((x, n) => x || byModel[n] !== null));
          // Позиции, которой не нашлось категории, — новая. Только когда чек
          // делится по позициям: иначе у него одна категория на всё.
          if (splitsByItem(receipt)) {
            void nc.suggest(names.flatMap((name, n) => (byModel[n] === null && !touched.current.has(n) ? [{ n, name }] : [])));
          }
        });
      }
    } catch (e) {
      setStage({
        kind: 'rejected',
        message: e instanceof Error ? e.message : 'Не удалось разобрать документ',
      });
    }
  };

  // Файл выбран ещё в записи операции — разбор начинается сразу.
  const started = useRef(false);
  useEffect(() => {
    if (!file || started.current) return;
    started.current = true;
    void take(file);
    // Файл один на всё время листа.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const confirm = async (): Promise<void> => {
    if (stage.kind !== 'review' || !catId) return;
    const items = stage.receipt.items ?? [];
    setStage({ kind: 'working', step: 'Записываю' });
    try {
      // Новые категории появляются вместе с тратами.
      const real = await nc.materialize(items.map((_, n) => itemCats[n] ?? catId));
      const { created, count } = await confirmReceiptImport({
        importId: stage.importId, accountId: accId, categoryId: catId,
        part: categories.find((c) => c.id === catId)?.defaultPart ?? 'need',
        parts: items.map((_, n) => partOf(n)),
        categories: items.map((_, n) => real(itemCats[n] ?? catId)),
      });
      onSaved();
      finish(created && count > 1
        ? `Записано ${plural(count, 'трата', 'траты', 'трат')} — по категориям чека`
        : 'Покупка записана');
    } catch (e) {
      setStage({ kind: 'rejected', message: e instanceof Error ? e.message : 'Не удалось записать' });
    }
  };

  const link = async (expenseId: string): Promise<void> => {
    if (stage.kind !== 'review') return;
    const { importId } = stage;
    setStage({ kind: 'working', step: 'Привязываю' });
    try {
      await linkReceiptImport({ importId, expenseId });
      onSaved();
      // Деньги уже были учтены в момент записи расхода: документ добавил
      // к покупке состав, и только это.
      finish('Чек добавлен к трате · баланс не изменился');
    } catch (e) {
      setStage({ kind: 'rejected', message: e instanceof Error ? e.message : 'Не удалось привязать' });
    }
  };

  const cancel = async (): Promise<void> => {
    if (stage.kind === 'review') await dropReceiptImport(stage.importId);
    onClose();
  };

  const picker = (label: string) => (
    <label className="btn pri file">
      {label}
      <input type="file" accept={DOC_ACCEPT}
        onChange={(e) => void take(e.target.files?.[0])} />
    </label>
  );

  // Пояснение листа — у заголовка: что принимаю, пока файл не выбран, и что
  // разобранный чек ещё ничего не записал.
  const tip = access === null || !view.allowed ? null
    : stage.kind === 'pick'
      ? `Принимаю PDF, PNG и JPEG до ${MAX_RECEIPT_BYTES / 1024 / 1024} МБ: кассовый `
        + 'чек, квитанцию или скриншот операций из приложения банка, выписку по '
        + 'счёту. Несколько чеков загружай по отдельности, а операции из банка '
        + 'запишу каждую своей строкой.'
      : stage.kind === 'review'
        ? 'Пока не подтвердишь — баланс не меняется: документ лежит отдельно от трат.'
        : null;

  return (
    <SheetWrap onClose={() => void cancel()}
      // Название — по тому, что в документе, а не по кнопке: сюда приходят и
      // чеки, и квитанции банка, и выписки, и снимки экрана банка.
      title={stage.kind === 'statement'
        ? (stage.operations.length === 1 ? 'Операция из банка' : 'Операции из банка')
        : stage.kind === 'review' ? 'Кассовый чек' : 'Документ'}
      tip={tip}
      {...(stage.kind === 'statement' ? {} : { className: 'rcpt' })}>
      {/* Файл, выбранный в записи операции, уже прошёл проверку доступа:
          ждать её второй раз незачем — сервер всё равно проверит сам. */}
      {access === null && !file ? (
        <>
          <div className="note">Проверяю доступ…</div>
          {/* Выход в ручную запись есть всегда — и пока ждём ответа тоже. */}
          <button className="btn ghost" onClick={onManual}>Записать вручную</button>
        </>
      ) : access !== null && !view.allowed ? (
        <>
          <div className="note">{view.message}</div>
          {/* Оплату предлагаем только когда дело в тарифе: «позже» и «нет сети»
              покупкой не лечатся. */}
          {view.offerUpgrade && (
            <button className="btn pri" onClick={onPlans}>Что даёт Pro</button>
          )}
          <button className="btn ghost" onClick={onManual}>Ввести расход вручную</button>
        </>
      ) : stage.kind === 'pick' ? (
        <>
          {picker('Выбрать файл')}
          <button className="btn ghost" onClick={onManual}>Записать вручную</button>
        </>
      ) : stage.kind === 'working' ? (
        <div className="note">{stage.step}…</div>
      ) : stage.kind === 'rejected' ? (
        <>
          <div className="note alarm">{stage.message}</div>
          {/* Трату из нераспознанного итога не создаём — выдуманная сумма
              хуже её отсутствия. */}
          {picker('Выбрать другой файл')}
          <button className="btn ghost" onClick={onManual}>Записать вручную</button>
        </>
      ) : stage.kind === 'refused' ? (
        <>
          <div className="note alarm">{stage.message}</div>
          {picker('Загрузить другой документ')}
          <button className="btn ghost" onClick={onManual}>Записать вручную</button>
        </>
      ) : stage.kind === 'statement' ? (
        <StatementReview state={{ ...state, categories }} {...(initialAccountId ? { initialAccountId: accId } : {})} operations={stage.operations} dropped={stage.dropped}
          onCancel={onClose}
          onDone={(message) => { onSaved(); finish(message); }} />
      ) : (
        <Review
          receipt={stage.receipt}
          matches={stage.matches}
          state={state}
          computed={stage.computed}
          preview={preview}
          categories={categories} setCategories={setCategories} nc={nc}
          itemCats={itemCats} sure={sure} sorting={sorting || nc.busy}
          onPick={(n, id) => {
            touched.current.add(n);
            nc.resolve(n);
            setItemCats((p) => p.map((x, j) => (j === n ? id : x)));
            setSure((p) => p.map((x, j) => (j === n ? true : x)));
          }}
          accId={accId} setAccId={setAccId}
          catId={catId} setCatId={setCatId}
          onConfirm={() => void confirm()}
          onLink={(id) => void link(id)}
          onCancel={() => void cancel()}
        />
      )}
      {stage.kind === 'review' && (
        <NewModeQuestion nc={nc} names={[...nc.pending.keys()].map((n) => stage.receipt.items?.[n]?.name ?? '')} />
      )}
    </SheetWrap>
  );
}

/**
 * Чек, прочитанный по напечатанному тексту.
 *
 * Здесь ничего не угадано: имя магазина, дата, состав и итог взяты из
 * документа, а фискальные реквизиты дают самый надёжный ключ повтора — один и
 * тот же чек, сохранённый дважды разными файлами, узнаётся по ним.
 */
function fromPrinted(fileHash: string, printed: FiscalReceipt): ImportedReceipt {
  const fiscal = printed.fiscal;
  return {
    fileHash,
    // Возврат прихода покупкой не станет: приложение спросит человека, а не
    // спишет деньги там, где они вернулись.
    kind: printed.kind === 'refund' ? 'refund' : 'sale',
    ...(Object.keys(fiscal).length > 0 ? { fiscal } : {}),
    ...(printed.merchant ? { merchant: printed.merchant } : {}),
    ...(printed.date ? { date: printed.date } : {}),
    ...(printed.time ? { time: printed.time } : {}),
    // Числа в чеке напечатаны рублями, а живут в приложении копейками.
    // Распознавание переводит их на сервере, напечатанный текст — здесь.
    ...(printed.total !== null ? { total: rub(printed.total) } : {}),
    ...(printed.items.length > 0
      ? { items: printed.items.map((i) => ({
        name: i.name, qty: i.qty, price: rub(i.price), sum: rub(i.sum),
      })) }
      : {}),
  };
}

/**
 * Чек возврата — одно поступление: деньги вернулись, а не ушли. Человек
 * запишет его поступлением в листе операций, как строку из банка.
 */
function refundOp(printed: FiscalReceipt): StatementOp[] {
  if (printed.total === null || printed.total <= 0) return [];
  return [{
    title: printed.merchant ?? 'Возврат',
    amount: rub(printed.total),
    direction: 'in',
    type: 'income',
    bankCategory: null,
    account: null,
    date: printed.date ?? null,
    time: printed.time ?? null,
    refund: true,
  }];
}

/**
 * Запасной путь: страницы рисуются и уходят распознаванию.
 *
 * Нужен сканированному документу — снимку, сохранённому в PDF, — и чеку
 * незнакомой вёрстки, в котором текст есть, но собрать из него позиции не
 * удалось. Этот путь стоит денег и единицы пакета, поэтому идёт вторым.
 */
async function fromPictures(file: File, doc: {
  fileHash: string; pages: { base64: string; mimeType: string }[];
}): Promise<Read> {
  const pages = doc.pages.length > 0 ? doc.pages : await renderDocument(file);
  return fromParsed(doc.fileHash, await parseDocument({ pages, fileHash: doc.fileHash, today: todayPd() }));
}

type Read = { account?: string | null; receipt: ImportedReceipt; computed: boolean; operations: StatementOp[]; dropped: number };

/** Ответ разбора — в документ и операции экрана. */
function fromParsed(fileHash: string, parsed: Awaited<ReturnType<typeof parseDocument>>): Read {
  return {
    account: parsed.account ?? null,
    operations: parsed.operations,
    dropped: parsed.droppedOperations,
    computed: parsed.totalFromItems,
    receipt: {
      fileHash,
      // Тип документа называет разбор, а не мы за него. Список операций,
      // принятый за покупку, стоил бы человеку траты на сумму пяти чужих.
      kind: parsed.kind,
      ...(parsed.merchant ? { merchant: parsed.merchant } : {}),
      ...(parsed.date ? { date: parsed.date as PlainDate } : {}),
      ...(parsed.time ? { time: parsed.time } : {}),
      ...(parsed.total !== null ? { total: kop(parsed.total) } : {}),
      ...(parsed.items.length > 0
        ? { items: parsed.items.map((i) => ({
          name: i.name, qty: i.qty, price: kop(i.price), sum: kop(i.sum),
        })) }
        : {}),
    },
  };
}

/**
 * Делится ли чек по позициям: только когда состав сходится с итогом до
 * копейки — так же решает запись. Иначе он записывается одной тратой с одной
 * категорией.
 */
function splitsByItem(receipt: ImportedReceipt): boolean {
  const items = receipt.items ?? [];
  return items.length > 0 && receipt.total !== undefined && items.reduce((a, it) => a + it.sum, 0) === receipt.total;
}

/** Что показать после классификации документа. */
function afterVerdict(
  verdict: ImportVerdict, receipt: ImportedReceipt, importId: string | undefined,
  computed = false,
): Stage {
  if (verdict.kind === 'duplicate') {
    return {
      kind: 'refused',
      message: verdict.reason === 'file'
        ? 'Этот чек уже загружен — вторая покупка из него не создаётся'
        : 'Такой чек уже есть: совпали реквизиты документа',
    };
  }
  if (verdict.kind === 'not_expense') return { kind: 'refused', message: verdict.message };
  if (verdict.kind === 'unusable') return { kind: 'refused', message: verdict.message };
  if (!importId) return { kind: 'refused', message: 'Документ не сохранился — попробуй ещё раз' };
  return {
    kind: 'review',
    receipt,
    importId,
    computed,
    matches: verdict.kind === 'may_match' ? verdict.candidates : [],
  };
}

/**
 * Карточка проверки: что нашлось в документе.
 *
 * Неизвестное остаётся пустым, а не угадывается. Пустая строка «продавец»
 * честнее выдуманного названия: человек видит, что именно нужно дополнить.
 */
function Review({
  receipt, matches, state, computed, preview, categories, setCategories, nc, itemCats, sure, sorting, onPick,
  accId, setAccId, catId, setCatId, onConfirm, onLink, onCancel,
}: {
  receipt: ImportedReceipt;
  /** Картинка документа для «Просмотреть». */
  preview: string | null;
  matches: MatchCandidate[];
  state: AppState;
  /** Итог посчитан по позициям: в документе его не было. */
  computed: boolean;
  categories: Category[]; setCategories: (v: Category[]) => void;
  /** Новые категории: предложены, но до «Записать» их нет в справочнике. */
  nc: NewCategories;
  /** Категория каждой позиции, в порядке чека. */
  itemCats: string[];
  sure: boolean[];
  /** Модель ещё подбирает категории. */
  sorting: boolean;
  onPick: (index: number, categoryId: string) => void;
  accId: string; setAccId: (v: string) => void;
  catId: string; setCatId: (v: string) => void;
  onConfirm: () => void;
  onLink: (expenseId: string) => void;
  onCancel: () => void;
}) {
  const arithmetic = checkItemsTotal(receipt);
  const items = receipt.items ?? [];
  const perItem = splitsByItem(receipt);
  const categoryCount = new Set(items.map((_, n) => itemCats[n] ?? catId)).size;
  const total: Money = receipt.total ?? ZERO;
  const whenLabel = receipt.date
    ? `${dayMonthYear(receipt.date)}${receipt.time ? `, ${receipt.time}` : ''}`
    : 'Дата не распознана';

  return (
    <>
      <ReceiptHead total={total} count={items.length} preview={preview} />
      <ReceiptSelectors accounts={state.accounts.filter((a) => !a.archived)} accId={accId} onAccount={setAccId}
        when={{ label: whenLabel }}
        {...(!perItem ? {
          // Состав не сходится с итогом или его нет: чек записывается одной
          // тратой, и категория у него одна — строкой той же карточки.
          extra: (
            <CategoryField categories={categories} kind="expense" value={catId} onChange={setCatId} onUpdated={setCategories}
              trigger={(open, current) => (
                <button className="rcselrow" onClick={open} aria-label={`Категория: ${current?.name ?? 'Выбрать'}`}>
                  <span className="rclabel">Категория</span>
                  <span className="rcvalue"><span className="rcvaltext">{current?.name ?? 'Выбрать'}</span></span>
                </button>
              )} />
          ),
        } : {})} />

      {/* Арифметику проверяет код, а не свободный ответ модели: расхождение
          показываем человеку, недостающий товар не придумываем. */}
      {!arithmetic.ok && <div className="note warn">{arithmetic.message}</div>}

      {/* Итога в документе не было. Сумма сложена из позиций — и если строка
          не прочиталась, покупка была больше. Сказать об этом обязаны: сверить
          такой итог не с чем, кроме памяти человека. */}
      {computed && (
        <div className="note warn">
          Итог в документе не напечатан — сумма сложена из позиций. Сверь её
          с той, что списал банк.
        </div>
      )}

      <SplitBanner show={perItem && categoryCount > 1} />
      {perItem && sorting && <div className="note">Подбираю категории…</div>}
      {perItem && <NewCatsAsk nc={nc} nameOf={(n) => items[n]?.name ?? ''} />}
      {perItem && <NewCatsLines nc={nc} ids={items.map((_, n) => itemCats[n] ?? catId)} />}
      {items.length > 0 && (
        <ReceiptItems lines={items} categories={nc.all} onCategories={setCategories}
          categoryOf={(n) => (perItem ? itemCats[n] ?? catId : null)}
          unsure={(n) => perItem && !sorting && !sure[n]}
          isNew={(n) => perItem && isNewCategory(itemCats[n])}
          onPick={onPick} />
      )}

      {matches.length > 0 && (
        <>
          <div className="blkhead"><b>Похоже на уже записанное
            {' '}<Tip>Если это та же покупка — свяжи их. Сумма второй раз не спишется.</Tip></b></div>
          {matches.map((m) => {
            const tx = state.transactions.find((t) => t.id === m.expenseId);
            return (
              <button className="li" key={m.expenseId} onClick={() => onLink(m.expenseId)}>
                <div className="nm">
                  <b>{tx?.note ?? 'Трата'}</b>
                  <span>{tx?.occurredDate} · {m.why}</span>
                </div>
                {tx && <div className="am num">{formatMoney(tx.amount)}</div>}
              </button>
            );
          })}
        </>
      )}

      <ReceiptBar total={total} disabled={receipt.total === undefined || !catId}
        label={`Записать ${formatMoney(total)}`} onSave={onConfirm} onCancel={onCancel} />
    </>
  );
}
